import type { ConceptChipApi } from '@mathflat/domain/@entities/ConceptChip/api'

import { mpEventInfo } from '@/constant'
import type { DefaultResponse } from '@/types'
import maxios from '@/utils/axios/maxios'

const achievementAnalysisApi = {
  ////// 그룹 유형 분석
  // 그룹 유형칩&성취도 조회
  getGroupConceptChipsAndAchievements: (
    params: ConceptChipApi.Request.ByGroupConceptChipsParams,
  ) => {
    return maxios.get<ConceptChipApi.Response.ByGroupConceptChip[]>(
      '/student-history/achievement/by-group/concept-chips',
      {
        params,
      },
    )
  },

  // 그룹 유형칩 상세 조회
  getGroupConceptChipDetail: ({
    conceptChipId,
    ...params
  }: ConceptChipApi.Request.GetGroupConceptChipDetailParams) => {
    return maxios.get<ConceptChipApi.Response.GroupConceptChipDetail>(
      `/student-history/achievement/by-group/concept-chips/${conceptChipId}`,
      {
        params,
      },
    )
  },

  // 그룹 유형칩 과거 성취도 조회
  getGroupPastAchievements: (params: ConceptChipApi.Request.GetGroupPastAchievementsParams) => {
    return maxios.get<ConceptChipApi.Response.ByGroupConceptChip[]>(
      `/student-history/achievement/by-group/log`,
      {
        params,
      },
    )
  },

  // 개인 맞춤형 취약유형칩 학습지 문제 추천 받기
  getGroupPersonalizedWeakConceptChip: (
    params: ConceptChipApi.Request.GetGroupPersonalizedWeakConceptChipParams,
  ) => {
    return maxios.get<ConceptChipApi.Response.GroupPersonalizedWeakConceptChip>(
      `/derivation/weak-concept-chip/personalized/by-group`,
      {
        params,
      },
    )
  },

  // 개인 맞춤형 취약유형칩 학습지 생성
  postGroupPersonalizedWeakConceptChip: (
    params: ConceptChipApi.Request.PostGroupPersonalizedWeakConceptChipParams,
  ) => {
    return maxios.post(`/worksheet/weak-concept-chip/personalized`, params)
  },

  // 그룹 공통형 취약유형칩 학습지 문제 추천 받기
  // 그룹 공통형 취약유형칩 학습지 생성은 POST /worksheet 이용
  getGroupCommonWeakConceptChip: (
    params: ConceptChipApi.Request.GetGroupCommonWeakConceptChipParams,
  ) => {
    return maxios.get<ConceptChipApi.Response.GroupCommonWeakConceptChip>(
      `/derivation/weak-concept-chip/group-common/by-group`,
      {
        params,
      },
    )
  },

  ///// 개인 유형별 분석
  // 유형칩 조회
  getConceptChips: (params: req.AchievementAnalysisChip) => {
    return maxios.get<DefaultResponse<res.ConceptChip[]>>('/concept/chips', {
      params: {
        ...params,
        mp_event_name: mpEventInfo.유형분석_유형칩_조회,
      },
    })
  },

  // 성취도 조회
  getAchievements: (params: req.Achievement, studentId: res.Student['id']) => {
    return maxios.get<DefaultResponse<res.Achievement[]>>(
      `/student-history/achievement/student/${studentId}`,
      {
        params: {
          ...params,
          mp_event_name: mpEventInfo.유형분석_학생성취도_조회,
        },
      },
    )
  },

  postAchievements: (data: req.CohortAchievement) => {
    return maxios.post<res.CohortAchievement>(
      `/student-history/achievement/student`,
      data.studentIds,
      {
        params: {
          curriculumKey: data.curriculumKey,
        },
      },
    )
  },

  getPastAchievements: (params: req.Achievement, studentId: res.Student['id']) => {
    return maxios.get<DefaultResponse<res.Achievement[]>>(
      `/student-history/achievement/student/${studentId}/log`,
      {
        params: {
          ...params,
          mp_event_name: mpEventInfo.유형분석_학생성취도_과거이력_조회,
        },
      },
    )
  },

  // 최근 학습 내역(10건) 조회
  getAchievementStudyLogs: (
    studentId: res.Student['id'],
    conceptChipId: res.ConceptChip['conceptChipId'],
    params?: req.AchievementStudyLog,
  ) => {
    return maxios.get<DefaultResponse<res.AchievementStudyLog[]>>(
      `/student-history/achievement/student/${studentId}/concept-chips/${conceptChipId}`,
      {
        params: {
          ...params,
          mp_event_name: mpEventInfo.유형분석_유형칩별_상세보기,
        },
      },
    )
  },

  postAchievementReport: (studentId: res.Student['id'], payload: req.AchievementReport) => {
    return maxios.post<DefaultResponse<null>>(`/report/achievement/${studentId}`, payload)
  },

  getAchievementReport: (studentId: res.Student['id']) => {
    return maxios.get<DefaultResponse<res.AchievementReportsData>>(
      `/report/achievement/${studentId}`,
      {
        params: {
          mp_event_name: mpEventInfo.유형분석_보고서_조회,
        },
      },
    )
  },

  deleteReports: (params: { reportIds: res.AchievementReport['id'][] }) => {
    return maxios.delete<DefaultResponse<null>>(`/report/achievement`, {
      params: {
        ...params,
        mp_event_name: mpEventInfo.유형분석_보고서_삭제,
      },
    })
  },
  getReportFile(params: req.DownloadAchievementReport) {
    return maxios.get<common.PresignedFile>(`/report/achievement/v2/download`, {
      params: {
        ...params,
      },
    })
  },

  // 반에 설정된 챌린지 조회하기
  getLessonClassChallenges: (lessonClassId: req.LessonClassChallenge['lessonClassId']) => {
    return maxios.get<res.LessonClassChallenges>(`/lesson-classes/${lessonClassId}/challenges`)
  },

  // 반에 챌린지 설정, 수정하기
  postLessonClassChallenges: ({ lessonClassId, ...body }: req.LessonClassChallenge) => {
    return maxios.post<res.LessonClassChallengeCurriculumInfo>(
      `/lesson-classes/${lessonClassId}/setting-challenges`,
      body,
    )
  },

  // 반에 설정된 챌린지 해제하기
  patchLessonClassChallengeStatus: (params: req.LessonClassChallengeStatus) => {
    return maxios.patch('/lesson-classes/settings/status', params)
  },
}

export default achievementAnalysisApi
